import type { Node } from '@tiptap/pm/model';
import { cellAround, CellSelection } from '@tiptap/pm/tables';
import { type Editor, type EditorStateSnapshot, useEditorState } from '@tiptap/react';
import { Database, KeyRound } from 'lucide-react';
import { forwardRef, useCallback, useMemo, useState } from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Lib ---
import { getTable } from '@/components/tiptap-editor/tiptap-node/table-node/lib/tiptap-table-utils';
import { cn, SR_ONLY } from '@/lib/tiptap-utils';

// --- UI ---
import { TableAlignMenu } from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-alignment-menu';
import { useTableClearRowColumnContent } from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-clear-row-column-content-button';
import { useTableMergeSplitCell } from '@/components/tiptap-editor/tiptap-node/table-node/ui/table-merge-split-cell-button';

// --- UI Primitives ---
import { Button } from '@/components/tiptap-editor/tiptap-ui-primitive/button';
import { Combobox, ComboboxList } from '@/components/tiptap-editor/tiptap-ui-primitive/combobox';
import { Menu, MenuButton, MenuContent, MenuGroup, MenuItem } from '@/components/tiptap-editor/tiptap-ui-primitive/menu';
import { Separator } from '@/components/tiptap-editor/tiptap-ui-primitive/separator';

// --- Icons ---
import { Grip4Icon } from '@/components/tiptap-editor/tiptap-icons/grip-4-icon';

import './table-cell-handle-menu.scss';

interface TableAction {
    icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
    label: string;
    onClick: () => void;
    isAvailable: boolean;
    isActive?: boolean;
    disabled?: boolean;
    shortcutBadge?: React.ReactNode;
}

type TableHeaderKeySnapshot = {
    show: boolean;
    primaryKey: boolean;
    foreignKey: boolean;
};

function tableHeaderKeySnapshotEquality(a: TableHeaderKeySnapshot, b: TableHeaderKeySnapshot | null): boolean {
    return a.show === b?.show && a.primaryKey === b?.primaryKey && a.foreignKey === b?.foreignKey;
}

type DatabaseTableToggleSnapshot = {
    show: boolean;
    isDatabaseTable: boolean;
};

function databaseTableToggleSnapshotEquality(a: DatabaseTableToggleSnapshot, b: DatabaseTableToggleSnapshot | null): boolean {
    return a.show === b?.show && a.isDatabaseTable === b?.isDatabaseTable;
}

/**
 * Hook to manage all table actions and their availability
 */
function useTableActions() {
    const mergeCellAction = useTableMergeSplitCell({ action: 'merge' });
    const splitCellAction = useTableMergeSplitCell({ action: 'split' });
    const clearContentAction = useTableClearRowColumnContent({ resetAttrs: true });

    const mergeAction: TableAction = {
        icon: mergeCellAction.Icon,
        label: mergeCellAction.label,
        onClick: mergeCellAction.handleExecute,
        isAvailable: mergeCellAction.canExecute,
    };

    const splitAction: TableAction = {
        icon: splitCellAction.Icon,
        label: splitCellAction.label,
        onClick: splitCellAction.handleExecute,
        isAvailable: splitCellAction.canExecute,
    };

    const clearAction: TableAction = {
        icon: clearContentAction.Icon,
        label: clearContentAction.label,
        onClick: clearContentAction.handleClear,
        isAvailable: clearContentAction.canClearRowColumnContent,
    };

    return {
        mergeAction,
        splitAction,
        clearAction,
    };
}

/**
 * Hook to manage table handle menu state and interactions
 */
function useTableCellHandleMenu({ editor, onOpenChange }: { editor: Editor | null; onOpenChange?: (isOpen: boolean) => void }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const closeMenu = useCallback(() => {
        setIsMenuOpen(false);
        onOpenChange?.(false);
        editor?.commands.unfreezeHandles();
    }, [editor, onOpenChange]);

    const handleMenuToggle = useCallback(
        (isOpen: boolean) => {
            setIsMenuOpen(isOpen);
            onOpenChange?.(isOpen);

            if (!editor) return;

            if (isOpen) {
                editor.commands.freezeHandles();
            } else {
                editor.commands.unfreezeHandles();
            }
        },
        [editor, onOpenChange],
    );

    return {
        isMenuOpen,
        handleMenuToggle,
        closeMenu,
    };
}

const TableActionItem = ({ action }: { action: TableAction }) => {
    const { icon: Icon, label, onClick, isActive = false, shortcutBadge, disabled = false } = action;

    return (
        <MenuItem
            render={
                <Button
                    variant="ghost"
                    data-active-state={isActive ? 'on' : 'off'}
                />
            }
            onClick={onClick}
            disabled={disabled}
        >
            <Icon className="tiptap-button-icon" />
            <span className="tiptap-button-text">{label}</span>
            {shortcutBadge}
        </MenuItem>
    );
};

const tableHeaderKeySelector = (ctx: EditorStateSnapshot<Editor | null>): TableHeaderKeySnapshot => {
    const ed = ctx.editor;
    if (!ed) {
        return { show: false, primaryKey: false, foreignKey: false };
    }
    const { state } = ed;
    const { selection } = state;

    let cellNode: Node | null;

    if (selection instanceof CellSelection) {
        const uniqueCells: { pos: number; node: Node }[] = [];
        const seenPos = new Set<number>();
        selection.forEachCell((node, pos) => {
            if (!seenPos.has(pos)) {
                seenPos.add(pos);
                uniqueCells.push({ pos, node });
            }
        });
        if (uniqueCells.length !== 1) {
            return { show: false, primaryKey: false, foreignKey: false };
        }
        cellNode = uniqueCells[0]!.node;
    } else {
        const $cell = cellAround(selection.$anchor);
        if (!$cell) {
            return { show: false, primaryKey: false, foreignKey: false };
        }
        cellNode = state.doc.nodeAt($cell.pos) ?? null;
    }

    if (!cellNode || cellNode.type.name !== 'tableHeader') {
        return { show: false, primaryKey: false, foreignKey: false };
    }

    const table = getTable(ed);
    if (!table || !table.node.attrs?.isDatabaseTable || table.node.attrs?.isLayoutGrid) {
        return { show: false, primaryKey: false, foreignKey: false };
    }

    return {
        show: true,
        primaryKey: Boolean(cellNode.attrs.primaryKey),
        foreignKey: Boolean(cellNode.attrs.foreignKey),
    };
};

const databaseTableToggleSelector = (ctx: EditorStateSnapshot<Editor | null>): DatabaseTableToggleSnapshot => {
    const ed = ctx.editor;
    if (!ed) {
        return { show: false, isDatabaseTable: false };
    }
    const table = getTable(ed);
    if (!table || table.node.attrs?.isLayoutGrid) {
        return { show: false, isDatabaseTable: false };
    }
    return {
        show: true,
        isDatabaseTable: Boolean(table.node.attrs?.isDatabaseTable),
    };
};

const TableActionMenu = ({ editor = null, onClose }: { editor?: Editor | null; onClose: () => void }) => {
    const { mergeAction, splitAction, clearAction } = useTableActions();

    const headerKeySnapshot = useEditorState({
        editor: editor ?? null,
        selector: tableHeaderKeySelector,
        equalityFn: tableHeaderKeySnapshotEquality,
    });
    const keyState = headerKeySnapshot ?? { show: false, primaryKey: false, foreignKey: false };

    const databaseTableSnapshot = useEditorState({
        editor: editor ?? null,
        selector: databaseTableToggleSelector,
        equalityFn: databaseTableToggleSnapshotEquality,
    });
    const dbTableState = databaseTableSnapshot ?? { show: false, isDatabaseTable: false };

    const databaseTableAction = useMemo<TableAction>(
        () => ({
            icon: Database,
            label: 'Database table',
            onClick: () => {
                editor?.chain().focus().toggleDatabaseTable().run();
            },
            isAvailable: dbTableState.show,
            isActive: dbTableState.isDatabaseTable,
            disabled: !editor?.isEditable,
        }),
        [editor, dbTableState.show, dbTableState.isDatabaseTable],
    );

    const primaryKeyAction = useMemo<TableAction>(
        () => ({
            icon: KeyRound,
            label: 'Primary key',
            onClick: () => {
                editor?.chain().focus().togglePrimaryKey().run();
            },
            isAvailable: keyState.show,
            isActive: keyState.primaryKey,
            disabled: !editor?.isEditable,
        }),
        [editor, keyState.show, keyState.primaryKey],
    );

    const foreignKeyAction = useMemo<TableAction>(
        () => ({
            icon: KeyRound,
            label: 'Foreign key',
            onClick: () => {
                editor?.chain().focus().toggleForeignKey().run();
            },
            isAvailable: keyState.show,
            isActive: keyState.foreignKey,
            disabled: !editor?.isEditable,
        }),
        [editor, keyState.show, keyState.foreignKey],
    );

    const hasMergeOrSplit = mergeAction.isAvailable || splitAction.isAvailable;

    return (
        <MenuContent
            autoFocusOnShow
            modal
            onClose={onClose}
        >
            <Combobox style={SR_ONLY} />
            <ComboboxList style={{ minWidth: '15rem' }}>
                {hasMergeOrSplit && (
                    <MenuGroup>
                        {mergeAction.isAvailable && <TableActionItem action={mergeAction} />}
                        {splitAction.isAvailable && <TableActionItem action={splitAction} />}
                        <Separator orientation="horizontal" />
                    </MenuGroup>
                )}

                {dbTableState.show && (
                    <>
                        <MenuGroup>
                            <TableActionItem action={databaseTableAction} />
                        </MenuGroup>
                        <Separator orientation="horizontal" />
                    </>
                )}

                {keyState.show && (
                    <>
                        <MenuGroup>
                            <TableActionItem action={primaryKeyAction} />
                            <TableActionItem action={foreignKeyAction} />
                        </MenuGroup>
                        <Separator orientation="horizontal" />
                    </>
                )}

                <MenuGroup>
                    <TableAlignMenu />
                    {clearAction.isAvailable && <TableActionItem action={clearAction} />}
                </MenuGroup>
            </ComboboxList>
        </MenuContent>
    );
};

interface TableCellHandleMenuProps extends React.ComponentPropsWithoutRef<'button'> {
    editor?: Editor | null;
    onOpenChange?: (isOpen: boolean) => void;
}

export const TableCellHandleMenu = forwardRef<HTMLButtonElement, TableCellHandleMenuProps>(
    ({ editor: providedEditor, onOpenChange, className, ...props }, ref) => {
        const { editor } = useTiptapEditor(providedEditor);
        const { isMenuOpen, handleMenuToggle, closeMenu } = useTableCellHandleMenu({
            editor,
            onOpenChange,
        });

        return (
            <Menu
                open={isMenuOpen}
                onOpenChange={handleMenuToggle}
                placement="bottom-start"
                trigger={
                    <MenuButton
                        ref={ref}
                        className={cn('expandable-menu-button', isMenuOpen && 'menu-opened', className)}
                        aria-label="Table cells option"
                        aria-haspopup="menu"
                        aria-expanded={isMenuOpen}
                        {...props}
                    >
                        <Grip4Icon className="tiptap-button-icon" />
                    </MenuButton>
                }
            >
                <TableActionMenu
                    editor={editor}
                    onClose={closeMenu}
                />
            </Menu>
        );
    },
);

TableCellHandleMenu.displayName = 'TableCellHandleMenu';

export { TableActionMenu };
